import { toast, Slide } from 'react-toastify';
import { apiClient } from './client';
import type { FileFolder } from '../../constants/file-types';
import { deleteFile as deleteFileApi } from '../../api/generated/sdk.gen';
import { compressImageIfNeeded } from '@/shared/utils/image-compression';
import { handleResponse } from './response-handler';

/**
 * Files API client for file upload and download operations
 *
 * Uses shared apiClient with 401 retry interceptor to ensure:
 * - File uploads auto-retry on token expiration
 * - Consistent error handling across all API requests
 * - No duplicate auth header logic
 *
 * @see client.ts - Shared apiClient with 401 retry and token refresh logic
 */
class FilesAPI {
    /**
     * Upload a file (image, document, etc.) with toast feedback
     * Uses shared apiClient → benefits from 401 retry interceptor
     *
     * Backend returns plain string URL as per OpenAPI spec.
     * Defensive check handles both string and object responses for compatibility.
     *
     * @param file - File to upload
     * @param folder - Folder to upload to
     * @returns Upload response URL string
     */
    async uploadFile(
        file: File | null | undefined,
        folder?: FileFolder
    ): Promise<string> {
        const toastId = toast.loading('Se incarca un fisier...', { transition: Slide });

        try {
            if (!file) {
                toast.update(toastId, {
                    render: 'Nu a fost selectat un fisier',
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                });
                throw new Error('Nu a fost selectat un fisier');
            }

            // Compress image if needed (returns original for non-images or small images)
            const processedFile = await compressImageIfNeeded(file);

            const formData = new FormData();
            formData.append('file', processedFile);

            const response = await apiClient.post('/api/files/upload', formData, {
                params: { folder },
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.dismiss(toastId);

            // Backend returns plain string URL, but handle both formats for compatibility
            const result = response.data;
            return typeof result === 'string' ? result : result.url;
        } catch (error) {
            console.error('File upload error:', error);
            toast.update(toastId, {
                render: 'Un fisier nu s-a putut incarca',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
            throw error;
        }
    }

    /**
     * Download an Excel report as blob
     * Uses shared apiClient → benefits from 401 retry interceptor
     * @param path - Report path/URL
     * @returns Blob response for download
     */
    async downloadExcelReport(path: string): Promise<Blob> {
        try {
            const response = await apiClient.get('/api/files/excel-reports', {
                params: { url: path },
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            console.error('Excel download error:', error);
            throw error;
        }
    }

    /**
     * Upload an image file (convenience method)
     * Backend returns plain string URL
     */
    async uploadImage(file: File | null | undefined): Promise<string> {
        return this.uploadFile(file, 'LESSON_IMAGES' as any);
    }

    /**
     * Upload a signature file (convenience method)
     * Backend returns plain string URL
     */
    async uploadSignature(file: File | null | undefined): Promise<string> {
        return this.uploadFile(file, 'PARENT_SIGNATURES' as any);
    }

    /**
     * Upload a document file (convenience method)
     * Backend returns plain string URL
     */
    async uploadDocument(file: File | null | undefined): Promise<string> {
        return this.uploadFile(file, 'DEFAULT' as any);
    }

    /**
     * Extract filename from Azure blob URL
     * @param url - Full Azure blob URL (e.g., https://...blob.core.windows.net/ecotic/lesson-pdfs/file.pdf)
     * @returns Filename without container (e.g., lesson-pdfs/file.pdf)
     */
    private extractFilename(url: string): string {
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname; // /ecotic/lesson-pdfs/file.pdf
            // Remove container name (/ecotic/)
            return pathname.replace('/ecotic/', '');
        } catch (error) {
            console.error('Invalid URL format:', error);
            throw new Error('Invalid file URL');
        }
    }

    /**
     * Delete a file from Azure Blob Storage
     * @param fileUrl - Full Azure blob URL of the file to delete
     * @returns Promise that resolves when file is deleted
     * @throws Error if user is not authorized (403) or deletion fails
     */
    async deleteFile(fileUrl: string): Promise<void> {
        try {
            const filename = this.extractFilename(fileUrl);

            // SDK-ul generat are ThrowOnError = false: un 403/404/500 se rezolva normal, ca
            // {error}. Fara handleResponse, deleteFileApi nu arunca niciodata la un esec HTTP.
            handleResponse(await deleteFileApi({
                query: { filename }
            }));
        } catch (error) {
            console.error('File deletion error:', error);
            throw error;
        }
    }
}

export const filesApi = new FilesAPI();
